import { findFirstNonEmptyString } from "@product/scmp-sdk";
import first from "lodash/first";
import type { FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { useCanonicalUrl } from "shared/hooks";

import { PageSeo } from "scmp-app/components/meta";
import {
  PostiesAdditionalLinkTags,
  PostiesTitle,
} from "scmp-app/components/posties/posties-seo/consts";
import type { videoPostiesSeoVideo$key } from "scmp-app/queries/__generated__/videoPostiesSeoVideo.graphql";

type Props = {
  reference: videoPostiesSeoVideo$key;
};

export const VideoPostiesSeo: FunctionComponent<Props> = ({ reference: reference_ }) => {
  const video = useFragment(
    graphql`
      fragment videoPostiesSeoVideo on Video {
        headline
        socialHeadline
        summary {
          text
        }
        subHeadline {
          text
        }
        body {
          text
        }
        images {
          style(filter: { style: "og_image_posties_article" }) {
            url
          }
        }
        urlAlias
      }
    `,
    reference_,
  );

  const pickedSummary = first(video.summary?.text?.split("\n"));
  const pickedSubHeadline = first(video.subHeadline?.text?.split("\n"));
  const pickedBody = first(video.body?.text?.split("\n"));
  const description = findFirstNonEmptyString(pickedSummary, pickedSubHeadline, pickedBody);
  const title = findFirstNonEmptyString(video.socialHeadline, video.headline);
  const image = first(video.images)?.style?.url ?? "";
  const { canonicalUrl } = useCanonicalUrl(video.urlAlias);
  return (
    <PageSeo
      additionalLinkTags={PostiesAdditionalLinkTags}
      additionalMetaTags={undefined}
      canonical={canonicalUrl}
      description={description}
      openGraph={{
        description,
        images: [{ url: image }],
        siteName: PostiesTitle,
        title,
        url: canonicalUrl,
      }}
      options={{
        customMerge(key) {
          if (key === "additionalLinkTags") {
            return (_, source: unknown[]) => source;
          }
        },
      }}
      title={title}
      twitter={undefined}
    />
  );
};

VideoPostiesSeo.displayName = "VideoPostiesSeo";
